import { useQueries, useQuery } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../../utils/apiConfig';
import { apiService } from '../../services/api';
import { integrationApiService, type IntegrationSyncResult } from '../../services/integrationApi';
import type { SyncScheduleDto } from '../../utils/syncSchedule';
import { REORDER_SUGGESTIONS_QUERY_KEY, reorderApi } from '../../components/reorder/reorderApi';

export interface StoreCatalogCounts {
  total: number;
  brands: string[];
  categories: string[];
  productTypes: string[];
  discontinuedInSync: number;
  missingImages: number;
}

export interface StoreMember {
  id?: string;
  userId?: string;
  name?: string;
  email?: string;
  role?: string;
}

export interface StoreSalesSummary {
  totalNetRevenue?: number;
  totalUnitsSold?: number;
  brandCount?: number;
}

export interface StoreIntegration {
  id: string;
  platform?: string;
  type?: string;
  status?: string;
  isActive?: boolean;
  lastSync?: string | { _seconds: number };
  lastSyncAt?: string | { _seconds: number };
  /** When the scheduler will next queue a run (`resolveNextScheduledSync` on the API). */
  syncSchedule?: SyncScheduleDto | null;
}

/** The `/integrations/:id/syncs` route spreads the raw Firestore run doc, which includes
 *  `productsTotal` for in-progress runs — not modeled on the shared `IntegrationSyncResult` DTO. */
export type SyncRun = IntegrationSyncResult & { productsTotal?: number };

export interface StoreIntegrationHistory {
  selectionProductCount: number;
  runs: SyncRun[];
}

export interface StoreOrganization {
  id: string;
  name?: string;
  contactInfo?: { primaryContact?: { name?: string; email?: string } };
  website?: string;
  status?: string;
}

export interface RebateReportGroup {
  name: string;
  unitsSold: number;
  totalRebate: number;
  programCount: number;
  lineCount: number;
}

export interface RebateReportSummary {
  totalUnits: number;
  totalRebateAmount: number;
  programCount: number;
  lineCount: number;
  unmatchedUnits: number;
  byBrand: RebateReportGroup[];
}

export interface ShopifyMapAuditSummary {
  status: 'running' | 'complete' | 'error';
  /** Products checked (a product may have multiple Shopify variants) — the right unit to
   *  compare against a catalog product count. `variantsChecked` counts variants instead and
   *  can exceed the product count for multi-variant catalogs; don't use it for that comparison. */
  mappingsChecked: number;
  mappingsTotal?: number;
  variantsChecked: number;
  belowMap: number;
  suspiciousDiscount: number;
  stalePrice: number;
  staleCompareAt: number;
  missingLivePrice: number;
  unmappedVariant?: number;
  startedAt?: string;
  completedAt?: string | null;
}

export interface ShopifyMapAuditFinding {
  hasIssue: boolean;
  /** Issue codes on the listing (`below_map`, `stale_price`, `unmapped_variant`, ...). */
  issues?: string[];
}

export interface ShopifyMapAuditResult {
  summary: ShopifyMapAuditSummary;
  findings: ShopifyMapAuditFinding[];
}

/** 401/403/404 mean "nothing to show yet" for a store with no organization context — everything else is a real failure. */
const isExpectedEmptyStatus = (status: number): boolean => status === 401 || status === 403 || status === 404;

export function useCurrentOrganization(orgKey: string, enabled: boolean) {
  return useQuery<StoreOrganization | null>({
    queryKey: ['user-organization', orgKey],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/organizations/me', { headers });
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return null;
        throw new Error(`Failed to load organization (${response.status})`);
      }
      const body = await response.json();
      return body?.data || null;
    },
    enabled,
    retry: false,
  });
}

export function useStoreCatalogCounts(orgKey: string, enabled: boolean) {
  return useQuery<StoreCatalogCounts | null>({
    queryKey: ['store-catalog-counts', orgKey],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/catalog-counts', { headers });
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return null;
        throw new Error(`Failed to load catalog counts (${response.status})`);
      }
      const body = await response.json();
      return body?.data || null;
    },
    enabled,
  });
}

export function useStoreTeamMembers(orgKey: string, enabled: boolean) {
  return useQuery<StoreMember[]>({
    queryKey: ['store-members', orgKey],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/members', { headers });
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return [];
        throw new Error(`Failed to load team members (${response.status})`);
      }
      const body = await response.json();
      return body?.data || [];
    },
    enabled,
    retry: false,
  });
}

const toYmd = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export function useStoreSalesSummary(orgKey: string, enabled: boolean) {
  return useQuery<StoreSalesSummary | null>({
    queryKey: ['store-sell-through-summary', orgKey],
    queryFn: async () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      const headers = await getAuthHeaders();
      const response = await makeApiCall(
        `/store/sell-through/summary?startDate=${toYmd(start)}&endDate=${toYmd(end)}`,
        { headers }
      );
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return null;
        throw new Error(`Failed to load sales summary (${response.status})`);
      }
      const body = await response.json();
      return (body?.data || body) || null;
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/** Rebate $ identified from matched sales in the trailing 30 days — there is no claim-lifecycle
 *  tracking (unclaimed/in-review/paid) anywhere in the backend yet, so this is the only real
 *  rebate figure available; do not fabricate claim states on top of it. */
export function useStoreRebateReport(orgKey: string, enabled: boolean) {
  return useQuery<RebateReportSummary | null>({
    queryKey: ['store-rebate-report', orgKey],
    queryFn: async () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      const headers = await getAuthHeaders();
      const response = await makeApiCall(
        `/store/sell-through/rebate-report?startDate=${toYmd(start)}&endDate=${toYmd(end)}`,
        { headers }
      );
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return null;
        throw new Error(`Failed to load rebate report (${response.status})`);
      }
      const body = await response.json();
      return (body?.data || body) || null;
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useStoreIntegrations(enabled: boolean) {
  return useQuery<StoreIntegration[]>({
    queryKey: ['store-integrations'],
    queryFn: async () => {
      const response = await apiService.getIntegrations();
      return response?.data || [];
    },
    enabled,
  });
}

/** Store-scoped MAP/price audit against the live Shopify catalog. Used for price-mismatch
 *  tiles only — not for "in sync" / "selected" / "blocked". Those come from integration history. */
export function useShopifyPricingAudit(integrationId: string | undefined, enabled: boolean) {
  return useQuery<ShopifyMapAuditResult | null>({
    queryKey: ['store-shopify-pricing-audit', integrationId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/store/shopify-pricing-audit?integrationId=${encodeURIComponent(integrationId!)}`, { headers });
      if (!response.ok) {
        if (isExpectedEmptyStatus(response.status)) return null;
        throw new Error(`Failed to load pricing audit (${response.status})`);
      }
      const body = await response.json();
      if (!body?.summary) return null;
      return { summary: body.summary, findings: body.findings || [] };
    },
    enabled: enabled && !!integrationId,
    // Matches StoreShopifyPricingAuditPage's own poll cadence while a run is in progress —
    // otherwise this card can sit stuck on partial counts after the audit actually finishes.
    refetchInterval: (query) => (query.state.data?.summary.status === 'running' ? 4000 : false),
  });
}

export function useStoreSyncRuns(integrationId: string | undefined, enabled: boolean) {
  return useQuery<SyncRun[]>({
    queryKey: ['store-sync-runs', integrationId],
    queryFn: async () => {
      const runs = await integrationApiService.getIntegrationSyncs(integrationId!, 50, 0);
      return runs as SyncRun[];
    },
    enabled: enabled && !!integrationId,
    refetchInterval: (query) => {
      const runs = (query.state.data as SyncRun[] | undefined) || [];
      return runs.some((r) => r.status === 'queued' || r.status === 'running') ? 5000 : false;
    },
  });
}

/** Same `/integrations/:id/history` payload as IntegrationDetailPage — selection size + runs. */
export function useStoreIntegrationHistory(integrationId: string | undefined, enabled: boolean) {
  return useQuery<StoreIntegrationHistory>({
    queryKey: ['store-integration-history', integrationId],
    queryFn: async () => {
      const hist = await integrationApiService.getIntegrationHistory(integrationId!);
      const summary = hist?.summary ?? {};
      const detailed = Array.isArray(hist?.detailed) ? hist.detailed : [];
      return {
        selectionProductCount: Math.max(0, Math.floor(Number(summary.selectionProductCount) || 0)),
        runs: detailed as SyncRun[],
      };
    },
    enabled: enabled && !!integrationId,
    refetchInterval: (query) => {
      const runs = query.state.data?.runs || [];
      return runs.some((r) => r.status === 'queued' || r.status === 'running') ? 5000 : false;
    },
  });
}

/** Most listing holds `/integrations/:id/shopify-listing-holds` returns per integration. */
const LISTING_HOLD_PAGE = 50;

export interface StoreListingHolds {
  count: number;
  /** A per-integration list hit its cap, so the true count may be higher. */
  capped: boolean;
  /** First Shopify integration with holds waiting. */
  integrationId: string | null;
}

/** Listing holds across every Shopify integration, for the dashboard's attention list. */
export function useStoreListingHolds(integrations: StoreIntegration[], enabled: boolean): StoreListingHolds {
  const shopifyIds = integrations.filter((i) => i.platform === 'shopify').map((i) => i.id);
  const results = useQueries({
    queries: shopifyIds.map((id) => ({
      queryKey: ['shopify-listing-holds', id],
      queryFn: () => integrationApiService.listShopifyListingHolds(id),
      enabled,
      staleTime: 60_000,
      retry: false,
    })),
  });
  let count = 0;
  let capped = false;
  let integrationId: string | null = null;
  results.forEach((result, idx) => {
    const holds = result.data?.length ?? 0;
    if (holds === 0) return;
    count += holds;
    if (holds >= LISTING_HOLD_PAGE) capped = true;
    if (!integrationId) integrationId = shopifyIds[idx];
  });
  return { count, capped, integrationId };
}

/**
 * Reorder suggestions for the home card (flag `predictive_reorder`, plan
 * `advancedAnalytics`; MOT-149). Same query key prefix as the Sales Report
 * table, so a count saved there refreshes the card. A plan-gate 403 is kept as
 * the error (no retry) and the card shows its upgrade line.
 */
export const useStoreReorderSuggestions = (orgKey: string, enabled: boolean) =>
  useQuery({
    queryKey: [...REORDER_SUGGESTIONS_QUERY_KEY, orgKey],
    queryFn: () => reorderApi.listSuggestions('Could not load reorder suggestions.', { limit: 50 }),
    enabled,
    retry: false,
  });
